import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Post } from '../../api/discussionService';
import styled from 'styled-components';

// Default avatar placeholder
const DEFAULT_AVATAR = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';

// Category icon and color mapping
const CATEGORY_CONFIG: Record<string, { icon: string; color: string }> = {
  general: { icon: '📢', color: '#6c757d' },
  questions: { icon: '❓', color: '#007bff' },
  testimonials: { icon: '✝️', color: '#28a745' },
  resources: { icon: '📚', color: '#fd7e14' },
  default: { icon: '📝', color: '#6c757d' }
};

interface PostListProps {
  posts: Post[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  onPageChange: (offset: number) => void;
}

const PostListContainer = styled.div`
  margin-top: 20px;
`;

const PostCard = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CategoryBadge = styled.div<{ bgColor?: string }>`
  position: absolute;
  top: -10px;
  right: 20px;
  background-color: ${props => props.bgColor || '#6c757d'};
  color: white;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 5px;
`;

const PostTitle = styled.h2`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 10px;
  margin-right: 80px;
`;

const PostMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textLight};
`;

const PostAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorAvatar = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-right: 10px;
`;

const PostDate = styled.span`
  font-style: italic;
`;

const PostPreview = styled.div`
  margin-bottom: 15px;
  color: ${props => props.theme.colors.text};
  line-height: 1.5;
`;

const PostFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid ${props => props.theme.colors.bgDark};
`;

const PostStats = styled.div`
  display: flex;
  gap: 15px;
`;

const PostStat = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${props => props.theme.colors.textLight};
`;

const ReadMoreButton = styled.button`
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  gap: 10px;
`;

const PageButton = styled.button<{ active?: boolean }>`
  background-color: ${props => props.active ? props.theme.colors.primary : '#fff'};
  color: ${props => props.active ? '#fff' : props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.primary};
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.bgLight};
  }
`;

const PostList: React.FC<PostListProps> = ({ posts, pagination, onPageChange }) => {
  const navigate = useNavigate();
  const { limit, offset, total } = pagination;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  
  // Add debug logging
  console.log('Post data received:', posts);
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const truncateContent = (content: string, maxLength = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };
  
  const handlePageChange = (page: number) => {
    const newOffset = (page - 1) * limit;
    onPageChange(newOffset);
  };
  
  const getCategoryConfig = (category: string) => {
    const normalizedCategory = category?.toLowerCase();
    return CATEGORY_CONFIG[normalizedCategory] || CATEGORY_CONFIG.default;
  };
  
  return (
    <PostListContainer>
      {posts.map(post => {
        const categoryConfig = getCategoryConfig(post.category);
        
        // Debug individual post
        console.log('Processing post author data:', {
          id: post.id,
          display_name: post.author_display_name,
          username: post.author_username,
          first_name: post.author_first_name,
          last_name: post.author_last_name
        });
        
        return (
          <PostCard key={post.id}>
            <CategoryBadge bgColor={categoryConfig.color}>
              {categoryConfig.icon} {post.category}
            </CategoryBadge>
            <PostTitle>{post.title}</PostTitle>
            <PostMeta>
              <PostAuthor>
                <AuthorAvatar 
                  src={post.author_avatar_url || DEFAULT_AVATAR} 
                  alt={post.author_display_name}
                />
                <span>
                  {post.author_display_name || 
                   (post.author_first_name || post.author_last_name ? 
                    `${post.author_first_name || ''} ${post.author_last_name || ''}`.trim() : 
                    post.author_username || 'Anonymous User')}
                </span>
              </PostAuthor>
              <PostDate>{formatDate(post.created_at)}</PostDate>
            </PostMeta>
            <PostPreview>{truncateContent(post.content)}</PostPreview>
            <PostFooter>
              <PostStats>
                <PostStat>
                  <span role="img" aria-label="likes">❤️</span> {post.likes_count}
                </PostStat>
                <PostStat>
                  <span role="img" aria-label="comments">💬</span> {post.comments_count}
                </PostStat>
              </PostStats>
              <ReadMoreButton 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Read More clicked for post:', {
                    postId: post.id,
                    path: `/community/posts/${post.id}`,
                    event: e
                  });
                  navigate(`/community/posts/${post.id}`, { replace: false });
                }}
              >
                Read More
              </ReadMoreButton>
            </PostFooter>
          </PostCard>
        );
      })}
      
      {totalPages > 1 && (
        <PaginationContainer>
          <PageButton 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </PageButton>
          
          {Array.from({ length: totalPages }).map((_, index) => (
            <PageButton
              key={index}
              active={currentPage === index + 1}
              onClick={() => handlePageChange(index + 1)}
            >
              {index + 1}
            </PageButton>
          ))}
          
          <PageButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </PageButton>
        </PaginationContainer>
      )}
    </PostListContainer>
  );
};

export default PostList; 